import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getZipCodes, updateServiceArea, getProProfile } from "../api";

export default function ServiceAreaPage() {
  const navigate = useNavigate();
  const [zipCodes, setZipCodes] = useState<any[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const [zips, profile] = await Promise.all([
          getZipCodes("Houston"),
          getProProfile()
        ]);
        setZipCodes(zips || []);
        setSelected((profile as any).serviceZipCodes || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  function toggleZip(zip: string) {
    setSelected(prev =>
      prev.includes(zip) ? prev.filter(z => z !== zip) : [...prev, zip]
    );
  }

  function selectAll() {
    setSelected(filtered.map(z => z.zipCode));
  }

  function clearAll() {
    setSelected([]);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateServiceArea(selected);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  }

  const filtered = zipCodes.filter(z =>
    z.zipCode.includes(search) ||
    z.neighborhood.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-4 pb-4 shadow-sm sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="text-gray-400 text-sm mb-2 block">← Back</button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-navy">Service Area</h1>
            <p className="text-xs text-gray-400 mt-0.5">{selected.length} zip codes selected</p>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="bg-mouna-blue text-white rounded-full px-5 py-2 text-sm font-bold">
            {saving ? "Saving..." : saved ? "✓ Saved!" : "Save"}
          </button>
        </div>

        {/* Search */}
        <div className="relative mt-3">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search zip code or neighborhood..."
            className="w-full bg-gray-100 rounded-full pl-10 pr-4 py-2 text-sm outline-none"/>
        </div>

        {/* Select/Clear all */}
        <div className="flex gap-3 mt-3">
          <button onClick={selectAll} className="text-xs text-mouna-blue font-bold">Select All ({filtered.length})</button>
          <span className="text-gray-300">|</span>
          <button onClick={clearAll} className="text-xs text-gray-400 font-bold">Clear All</button>
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        <div className="bg-blue-pale rounded-2xl p-4 mb-4">
          <div className="text-xs text-gray-500">
            <span className="font-bold text-navy">📍 Houston, TX</span> — Select the zip codes where you want to receive leads. You will only be matched with clients in these areas.
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2">
          {filtered.map(zip => (
            <button key={zip.zipCode} onClick={() => toggleZip(zip.zipCode)}
              className={"w-full flex items-center justify-between px-4 py-3 rounded-2xl border-2 transition-all " +
                (selected.includes(zip.zipCode)
                  ? "bg-mouna-blue border-mouna-blue text-white"
                  : "bg-white border-gray-200 text-navy")}>
              <div className="flex items-center gap-3 text-left">
                <div className={"w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 " +
                  (selected.includes(zip.zipCode) ? "border-white bg-white" : "border-gray-300")}>
                  {selected.includes(zip.zipCode) && <div className="w-2.5 h-2.5 rounded-full bg-mouna-blue"></div>}
                </div>
                <div>
                  <div className="font-bold text-sm">{zip.zipCode}</div>
                  <div className={`text-xs ${selected.includes(zip.zipCode) ? "text-blue-200" : "text-gray-400"}`}>
                    {zip.neighborhood}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {filtered.length === 0 && !loading && (
          <div className="text-center py-8">
            <div className="text-3xl mb-2">🔍</div>
            <div className="text-sm text-gray-400">No zip codes found</div>
          </div>
        )}
      </div>

      {/* Floating save button */}
      {selected.length > 0 && (
        <div className="fixed bottom-6 left-5 right-5">
          <button onClick={handleSave} disabled={saving}
            className="w-full bg-mouna-blue text-white rounded-full py-4 font-bold text-sm shadow-lg">
            {saving ? "Saving..." : saved ? "✓ Saved!" : `Save ${selected.length} Zip Codes`}
          </button>
        </div>
      )}
    </div>
  );
}
